import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  addComposerLayer,
  isTopLayer,
  readComposerLayers,
  stateWithLayers,
  type ComposerLayer,
} from '../lib/composerLayers';

const NONE: ComposerLayer[] = [];

/**
 * Слои строки создания на телефоне — записи в истории через роутер (ТЗ главной § 7): превью этапа 1,
 * этап 2 и меню модулей. Открыть — `navigate(путь, {state: {composerLayers: [...открытые, слой]}})`;
 * что открыто, выводится только из `location.state`, поэтому системная «Назад» сама закрывает верхний
 * слой. Закрыть кнопкой — `navigate(-1)`, после «Создать» — назад ровно на фактическую глубину.
 *
 * `navigate(-n)` долетает позже (popstate), а запись слоя — сразу. Поэтому стек того, что будет в
 * истории, держим сами: второй «назад» до приземления первого не уходит (иначе ушли бы с главной),
 * а открытие, пока «назад» в пути, ждёт его приземления — иначе новая запись встала бы перед ним.
 * Компьютер слоёв не заводит (`enabled = false`).
 */
export function useComposerLayers(enabled: boolean) {
  const location = useLocation();
  const navigate = useNavigate();
  const fromState = useMemo(() => readComposerLayers(location.state), [location.state]);
  const layers = enabled ? fromState : NONE;

  const stack = useRef<ComposerLayer[]>(fromState);
  const popInFlight = useRef(false);
  /** «Назад», запрошенные, пока прежний ещё в пути: уйдут одним шагом после его приземления. */
  const morePops = useRef(0);
  const queued = useRef<ComposerLayer[]>([]);
  const loc = useRef(location);
  loc.current = location;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const pushNow = useCallback(
    (layer: ComposerLayer) => {
      const next = addComposerLayer(stack.current, layer);
      if (!next) return;
      stack.current = next;
      const { pathname, search, state } = loc.current;
      navigate(pathname + search, { state: stateWithLayers(state, next) });
    },
    [navigate],
  );

  const pop = useCallback(
    (count: number) => {
      const k = Math.min(count, stack.current.length);
      if (k <= 0) return;
      stack.current = stack.current.slice(0, stack.current.length - k);
      queued.current = [];
      if (popInFlight.current) {
        morePops.current += k;
        return;
      }
      popInFlight.current = true;
      navigate(-k);
    },
    [navigate],
  );

  // Адрес сменился: долетел наш переход или ученик нажал системную «Назад» — стек снова из истории.
  useLayoutEffect(() => {
    if (popInFlight.current) {
      popInFlight.current = false;
      if (morePops.current > 0) {
        const k = morePops.current;
        morePops.current = 0;
        popInFlight.current = true;
        navigate(-k);
        return;
      }
    }
    stack.current = readComposerLayers(location.state);
    if (queued.current.length && enabledRef.current) {
      const list = queued.current;
      queued.current = [];
      list.forEach(pushNow);
    }
    // Только на смену записи истории.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const open = useCallback(
    (layer: ComposerLayer) => {
      if (!enabledRef.current) return;
      if (popInFlight.current) {
        if (!queued.current.includes(layer)) queued.current.push(layer);
        return;
      }
      pushNow(layer);
    },
    [pushNow],
  );

  /** Закрыть кнопкой: только верхний слой (`navigate(-1)` снимает именно его). */
  const close = useCallback(
    (layer: ComposerLayer) => {
      if (isTopLayer(stack.current, layer)) pop(1);
    },
    [pop],
  );

  /** После успешного «Создать»: назад ровно на фактическую глубину. */
  const closeAll = useCallback(() => pop(stack.current.length), [pop]);

  /**
   * В истории слои, а показать нечего (перезагрузка — слово не хранится, «Назад» в старую запись,
   * строка заблокировалась): заменить запись пустыми слоями. Один лишний шаг «Назад» допустим.
   */
  const reset = useCallback(() => {
    if (popInFlight.current) return;
    const { pathname, search, state } = loc.current;
    if (!readComposerLayers(state).length) return;
    stack.current = [];
    queued.current = [];
    navigate(pathname + search, { replace: true, state: stateWithLayers(state, []) });
  }, [navigate]);

  // Заход на главную со слоями в истории (перезагрузка, «Назад» с другой страницы) или строка без
  // слоёв (компьютер, блокировка) — слоёв нет.
  useEffect(() => {
    if (fromState.length && !enabled) reset();
  }, [enabled, fromState.length, reset]);
  useEffect(() => {
    reset();
    // Один раз при заходе: показать нечего — слово не хранится.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const has = useCallback((layer: ComposerLayer) => layers.includes(layer), [layers]);

  return { layers, has, open, close, closeAll, reset };
}

export type ComposerLayers = ReturnType<typeof useComposerLayers>;
